<ng-container [formGroup]="formGroup">
  <div class="section">
    <pdq-heading element="h5">Display Options</pdq-heading>
    <div class="controls-wrapper">
      <div class="input-group">
        <div class="radio" formGroupName="settings">
          <div class="radio__header">
            <div class="radio__title">Minimum Threshold Type</div>
            <div class="radio__description">(When to show this upsell)</div>
          </div>
          <pdq-radio-group formControlName="thresholdType">
            @for (thresholdTypeOption of thresholdTypeOptions; track thresholdTypeOption) {
              <pdq-radio-button
                [value]="thresholdTypeOption.value"
                [checked]="
                  thresholdTypeOption.checked(formGroup.get('settings.thresholdType')?.value) ||
                  thresholdTypeOption.defaultChecked
                "
                [text]="thresholdTypeOption.text">
              </pdq-radio-button>
            }
          </pdq-radio-group>
        </div>
      </div>

      <div class="upsell-title">
        <div class="rich-textarea-container-wrapper">
          <div class="rich-textarea-container">
            <div class="rich-text-area-header-wrapper">
              <label class="rich-textarea-label">Message Displayed When Shipping Threshold Is Reached</label>
              @if (formGroup.get('settings.thresholdType').value !== UpsellThresholdTypes.NoThreshold) {
                <pdq-button
                  class="eye-preview-button"
                  variant="empty"
                  [icon]="isPreviewThresholdMode ? 'preview_eye_crossed' : 'preview_eye'"
                  [iconColor]="isPreviewThresholdMode ? 'var(--on-surface-default)' : 'var(--on-surface-light)'"
                  (click)="togglePreviewMode()">
                </pdq-button>
              }
            </div>
            <rich-textarea
              #textWithoutThreshold
              [initialContent]="formGroup.get('settings.textWithoutThreshold').value"
              placeholder="Enter your message here..."
              [enableLinkInsert]="true"
              [disabledFormats]="['underline']"
              (contentChange)="formGroup.get('settings.textWithoutThreshold').setValue($event)">
            </rich-textarea>
            @if (
              formGroup.get('settings.textWithoutThreshold').invalid &&
              formGroup.get('settings.textWithoutThreshold').errors?.required
            ) {
              <span class="error-message">Required</span>
            }
          </div>
        </div>
      </div>

      <div class="show-message-if-free-shipping-container" formGroupName="settings">
        <pdq-checkbox
          class="show-message-if-free-shipping-checkbox"
          [formControl]="formGroup.get('settings.showMessageIfFreeShippingAvailable')"
          label="Show Message If Free Shipping Is Available"
          labelSize="medium"
          [checked]="true"></pdq-checkbox>
      </div>
      <div class="show-message-if-free-shipping-description">
        Show this message when any free shipping option is available - even if the threshold isn't met
      </div>

      <div>
        <div class="upsell-title">
          <div class="rich-textarea-container-wrapper">
            <div class="rich-textarea-container">
              <div class="rich-text-area-header-wrapper">
                <label class="rich-textarea-label">Message Displayed When Shipping Threshold Is Not Reached</label>
                @if (formGroup.get('settings.thresholdType').value !== UpsellThresholdTypes.NoThreshold) {
                  <pdq-button
                    class="eye-preview-button"
                    variant="empty"
                    [icon]="!isPreviewThresholdMode ? 'preview_eye_crossed' : 'preview_eye'"
                    [iconColor]="!isPreviewThresholdMode ? 'var(--on-surface-default)' : 'var(--on-surface-light)'"
                    (click)="togglePreviewMode()">
                  </pdq-button>
                }
              </div>
              <rich-textarea
                #textWithThreshold
                [initialContent]="formGroup.get('settings.textWithThreshold').value"
                placeholder="Enter your message here..."
                [enableLinkInsert]="true"
                [class.disabled]="formGroup.get('settings.thresholdType').value === UpsellThresholdTypes.NoThreshold"
                [disabledFormats]="['underline']"
                (contentChange)="formGroup.get('settings.textWithThreshold').setValue($event)">
              </rich-textarea>
              @if (formGroup.get('settings').errors?.pattern) {
                <span class="error-message">{{ formGroup.get('settings').errors?.pattern }}</span>
              }
            </div>
          </div>
        </div>
        @if (
          formGroup
            .get('settings.textWithThreshold')
            .value?.includes(UpsellFreeShippingThresholdReplaceValues.AmountLeft)
        ) {
          <span class="description"
            >Use the tag {{ UpsellFreeShippingThresholdReplaceValues.AmountLeft }} to dynamically display the $ amount
            left to reach the threshold</span
          >
        }
        @if (
          formGroup
            .get('settings.textWithThreshold')
            .value?.includes(UpsellFreeShippingThresholdReplaceValues.ItemsLeft)
        ) {
          <span class="description"
            >Use the tag {{ UpsellFreeShippingThresholdReplaceValues.ItemsLeft }} to dynamically display the # of items
            left to reach the threshold</span
          >
        }
      </div>

      <div class="compare-at-price-checkbox-container" formGroupName="settings">
        <pdq-checkbox
          class="compare-at-price-checkbox"
          [formControl]="formGroup.get('settings.showCompareAtPrice')"
          label="Show 'Compare at price'"
          labelSize="medium"
          icon="attention"
          description="Displays the 'Compare At Price' if it's higher than the product price (e.g. $25.00). If the 'Compare At Price' is lower than or equal to the current price, it will not be shown."
          [checked]="true"></pdq-checkbox>
      </div>
    </div>
  </div>

  <div class="section">
    <pdq-heading element="h5">Appearance</pdq-heading>
    <div class="controls-wrapper">
      <div class="radio" formGroupName="style">
        <div class="radio__title">Product Display Type</div>
        <pdq-radio-group formControlName="displayType">
          @for (displayTypeOption of displayTypeOptions; track displayTypeOption) {
            <pdq-radio-button
              [value]="displayTypeOption.value"
              [checked]="formGroup.get('style.displayType')?.value === displayTypeOption.value"
              [text]="displayTypeOption.text"
              [tooltip]="displayTypeOption.tooltip"
              [tooltipIcon]="displayTypeOption.tooltipIcon">
            </pdq-radio-button>
          }
        </pdq-radio-group>
      </div>
      <div class="input-group">
        @if (formGroup.get('style.displayType')?.value === 'row') {
          <div formGroupName="style">
            <pdq-input-arrows
              class="number-of-rows-input"
              [formControl]="formGroup.get('style.numberOfRowsInRowDisplayType')"
              label="Number of rows"
              [min]="1"
              [max]="5"
              widthSize="fullwidth"
              [precision]="0"
              [step]="1"></pdq-input-arrows>
          </div>
        }
        <pdq-input
          [formControl]="formGroup.get('settings.textAddToOrder')"
          label="Button Display Text"
          [invalid]="formGroup.get('settings.textAddToOrder').invalid"
          [invalidMsg]="formGroup.get('settings.textAddToOrder').errors?.required ? 'required' : ''"></pdq-input>
      </div>
      <div class="input-group" formGroupName="style">
        <pdq-dropdown
          title="Button Appearance"
          formControlName="buttonAppearance"
          [defaultOption]="
            formGroup.get('style.buttonAppearance').value | primitiveDropdownOptionPipe: appearanceToDisplayText
          "
          [options]="Appearances | primitiveDropdownOptionsPipe: appearanceToDisplayText"></pdq-dropdown>
        <pdq-input-color
          label="Progress bar color"
          [control]="formGroup.get('settings.progressBarColor')"
          [formControl]="formGroup.get('settings.progressBarColor')"></pdq-input-color>
      </div>
      <div
        class="droplist"
        [cdkDropListData]="additionalDisplayTextsFormArray?.value"
        cdkDropList
        (cdkDropListDropped)="drop($event)">
        @for (
          additionalDisplayText of additionalDisplayTextsFormArray?.controls;
          track additionalDisplayText;
          let i = $index
        ) {
          @let displayTextGroup = additionalDisplayText | asFormGroup;
          <div [cdkDragData]="additionalDisplayText.value" cdkDrag>
            <form [formGroup]="displayTextGroup">
              <div class="drag-content-wrapper">
                <div cdkDragHandle class="drag-wrapper">
                  @for (i of dragHandleRectangles; track $index) {
                    <div class="drag-rectangle"></div>
                  }
                </div>
                <div class="additional-display-text-content">
                  <div class="additional-display-text-content__title">
                    <h4>
                      Display
                      {{ displayTextGroup.get('type')?.value | toAdditionalDisplayLabelText }}
                    </h4>
                    <div>
                      <pdq-slide-toggle
                        class="slide-toggle"
                        [formControl]="displayTextGroup.get('display')"></pdq-slide-toggle>
                    </div>
                  </div>
                  @if (displayTextGroup.get('display').value) {
                    <pdq-input
                      [formControl]="displayTextGroup.get('text')"
                      [label]="
                        (displayTextGroup.get('type')?.value | toAdditionalDisplayLabelText | titlecase) +
                        ' display text'
                      "
                      type="text"
                      [invalid]="displayTextGroup.get('text').invalid"
                      [invalidMsg]="displayTextGroup.get('text').errors?.additionalPattern"></pdq-input>
                    <span
                      >Use the tag {{ '{' + displayTextGroup.get('type')?.value + '}' }} to dynamically display the
                      {{ displayTextGroup.get('type')?.value | toAdditionalDisplayLabelText }}</span
                    >
                  }
                </div>
                <span></span>
              </div>
            </form>
          </div>
        }
      </div>
    </div>
  </div>
</ng-container>
